/**
 * /auditoria — navegador do audit_log (GUPM-138).
 *
 * "Quem mexeu em qual registro" — audit trail técnico (ANM Res. 95/2022),
 * separado do Log de Eventos operacional (M10). Acesso perfil B (RoleGuard
 * em App.tsx + gate na view `audit_log_public` no DB).
 *
 * Filtros: Entidade · Ação · Ator · Quem · Período — TODOS aplicados no banco
 * (GUPM-335, adendo 03/10). A lista é paginada ("Carregar mais"). Antes a tela
 * trazia as 200 linhas mais recentes e filtrava no navegador; em produção isso
 * cobria 1 dia e meio e escondia 0 de 53 TARPs do mês. Linha/detalhe
 * expansível em `AuditRow`.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataSources } from '@/services/datasources';
import type { AuditFilter } from '@/services/datasources/auditoria';
import {
  mensagemDeFalhaDaAuditoria,
  opcoesDeAcao,
  opcoesDeEntidade,
  type FiltroQuem,
} from '@/domain/auditoria/filtros';
import { diaLocalMenosDias, janelaLocalDeDias } from '@/domain/periodo';
import type { ChecklistItem } from '@/domain/shift-handover/types';
import { useAtoresDaAuditoria, useAuditLog } from './hooks';
import { AuditRow } from './AuditRow';
import { FiltrosDaAuditoria, type ValoresDosFiltros } from './FiltrosDaAuditoria';

const ENTIDADES = opcoesDeEntidade();
const ACOES = opcoesDeAcao();


export default function AuditoriaPage() {
  const { shiftHandover } = useDataSources();

  const [filtros, setFiltros] = useState<ValoresDosFiltros>({
    entidade: '',
    acao: '',
    ator: '',
    quem: '',
    de: diaLocalMenosDias(30),
    ate: diaLocalMenosDias(0),
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Dia do filtro = dia do fuso de quem lê, não o dia-calendário UTC.
  const serverFilter = useMemo<AuditFilter>(
    () => ({
      ...janelaLocalDeDias(filtros.de, filtros.ate),
      entities: filtros.entidade ? filtros.entidade.split(',') : undefined,
      action: filtros.acao || undefined,
      actorId: filtros.ator || undefined,
      quem: (filtros.quem || undefined) as FiltroQuem | undefined,
    }),
    [filtros],
  );
  const { data: paginas = [], isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useAuditLog(serverFilter);
  const entries = useMemo(() => paginas.flatMap((p) => p.entries), [paginas]);
  const { data: atores = [] } = useAtoresDaAuditoria();

  // Catálogo do checklist p/ resolver labels no drill-down de trocas de plantão.
  const { data: catalog = [] } = useQuery<ChecklistItem[]>({
    queryKey: ['shift-checklist-catalog-audit'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => shiftHandover.listChecklistItems(),
  });
  const catalogById = useMemo(() => {
    const m = new Map<string, ChecklistItem>();
    for (const c of catalog) m.set(c.id, c);
    return m;
  }, [catalog]);

  return (
    <div className="space-y-4">
      <header>
        <p className="text-xs uppercase tracking-wide text-jf-dim">Auditoria</p>
        <h1 className="text-2xl font-bold text-white">Trilha de Auditoria</h1>
        <p className="text-sm text-jf-muted">
          Quem fez o quê, quando e de onde — acessos e mudanças de registro, com o antes e o depois. Trilha imutável.
        </p>
      </header>

      <FiltrosDaAuditoria
        valores={filtros}
        onChange={(campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }))}
        entidades={ENTIDADES}
        acoes={ACOES}
        atores={atores}
      />

      {error && (
        <div role="alert" className="rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {mensagemDeFalhaDaAuditoria(error as { code?: string; message: string })}
        </div>
      )}

      <div className="text-xs text-jf-muted">
        {isLoading
          ? 'Carregando…'
          : `${entries.length} registro${entries.length !== 1 ? 's' : ''} exibido${entries.length !== 1 ? 's' : ''}${hasNextPage ? ' — há mais' : ''}`}
      </div>

      {/* Tabela */}
      <section className="rounded-lg border border-jf-border bg-jf-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-jf-surface2 text-[10px] uppercase tracking-wide text-jf-dim">
              <tr>
                <th className="px-3 py-2 text-left">Quando</th>
                <th className="px-3 py-2 text-left">Módulo</th>
                <th className="px-3 py-2 text-left">Entidade</th>
                <th className="px-3 py-2 text-left">Ação</th>
                <th className="px-3 py-2 text-left">Ator</th>
                <th className="px-3 py-2 text-left">IP</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && entries.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-jf-muted text-sm">
                    Nenhum registro no período/filtros.
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <AuditRow
                  key={e.id}
                  entry={e}
                  expanded={expandedId === e.id}
                  catalogById={catalogById}
                  onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-md border border-jf-border bg-jf-surface2 px-4 py-2 text-sm text-white hover:border-jf-yellow disabled:opacity-60"
          >
            {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        </div>
      )}

      <p className="text-xs text-jf-dim">
        Export PDF/XLSX + retenção 5 anos (SharePoint JFB) — ver{' '}
        <a
          href="https://jfbrasil.youtrack.cloud/issue/GUPM-140"
          target="_blank"
          rel="noreferrer"
          className="text-jf-yellow hover:underline"
        >
          GUPM-140
        </a>
        .
      </p>
    </div>
  );
}
